import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';
import { Socials } from './socials';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from '@remix-run/react';
import Sprite from '../sprites/sprite';

interface NavigationProps {
  isDark?: boolean;
  shouldShowNav?: boolean;
}

// Define navigation structure with children
const navigationItems = [
  {
    title: 'Magh Mela 2026',
    path: '/magh-mela-2026',
    children: [
      { title: 'Who We Are', path: '/magh-mela-2026/who-we-are' },
      { title: 'Paush Purnima', path: '/magh-mela-2026/paush-purnima' },
      { title: 'Makar Sankranti', path: '/magh-mela-2026/makar-sankranti' },
      { title: 'Mauni Amavasya', path: '/magh-mela-2026/mauni-amavasya' },
      { title: 'Basant Panchami', path: '/magh-mela-2026/basant-panchami' },
      { title: 'Maghi Purnima', path: '/magh-mela-2026/maghi-purnima' },
      { title: 'Maha Shivratri', path: '/magh-mela-2026/maha-shivratri' },
    ],
  },
  {
    title: 'Accommodations',
    path: '/accommodations',
    children: [
      {
        title: 'Sanskriti Vann Sangham (Dormitory)',
        path: '/accommodations/mudhouse-dormitory',
      },
      {
        title: 'Sanskriti Vann Mandapam (Premium Tents)',
        path: '/accommodations/premium-twin-tent',
      },
      {
        title: 'Sanskriti Vann Kutiram (Mudhouses)',
        path: '/accommodations/traditional-mudhouse',
      },
    ],
  },
  { title: 'Contact', path: '/contact-us' },
];

export const Navigation: React.FC<NavigationProps> = ({
  isDark = false,
  shouldShowNav = false,
}) => {
  return <WebNavigation isDark={isDark} shouldShowNav={shouldShowNav} />;
};

const WebNavigation: React.FC<NavigationProps> = ({
  isDark = false,
  shouldShowNav = false,
}) => {
  const [showNav, setShowNav] = React.useState(shouldShowNav);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation(['home', 'common']);

  const listener = React.useCallback(() => {
    if (!shouldShowNav) {
      setShowNav(window.scrollY >= (isDark ? 0.1 : 0.3) * window.innerHeight);
    }
  }, [isDark, shouldShowNav]);

  React.useEffect(() => {
    listener();
    window.addEventListener('scroll', listener);
    return () => window.removeEventListener('scroll', listener);
  }, [listener]);

  // Disable body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleBookingClick = useCallback(() => {
    if (isInquiryModeEnabled) {
      onOpenInquiry();
    } else {
      navigate('/packages/reservation');
    }
  }, [navigate]);

  return (
    <>
      {/* Top Contact Bar with CTA */}
      <div
        className={clsx(
          'fixed top-0 left-0 w-full z-40 transition-all duration-300',
          showNav
            ? 'bg-emerald-800 text-white'
            : 'bg-black/30 backdrop-blur-sm text-white',
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm">
            <span className="flex items-center gap-2">
              <span className="font-medium">For bookings:</span>
              <a
                href="tel:+918485835675"
                className="hover:underline flex items-center gap-1"
              >
                <Sprite
                  name="phone"
                  width="16"
                  height="16"
                  className="w-3.5 h-3.5 fill-current"
                />
                <span>/</span>
                <Sprite name="whatsapp" width="24" height="24" className="w-4 h-4" />
                <span>+91 84858 35675</span>
              </a>
            </span>
            {isInquiryModeEnabled ? (
              <button
                onClick={handleBookingClick}
                className="hidden sm:inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1 rounded-full transition-colors font-medium"
              >
                {t('common:express-interest')}
              </button>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => onOpenInquiry()}
                  className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1 rounded-full transition-colors font-medium"
                >
                  {t('common:express-interest')}
                </button>
                <button
                  onClick={handleBookingClick}
                  className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 px-3 py-1 rounded-full transition-colors font-semibold"
                >
                  {t('common:book-now')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={clsx(
          'fixed top-8 sm:top-10 left-0 w-full z-30 transition-all duration-300',
          showNav
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-emerald-100'
            : 'bg-transparent',
        )}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16 w-full">
            {/* Logo */}
            <a href="/" className="flex items-center group">
              <span
                className={clsx(
                  'text-xl sm:text-2xl font-serif tracking-wide transition-colors',
                  showNav ? 'text-emerald-800' : 'text-white',
                  isDark && 'text-emerald-800',
                )}
              >
                kumbh sukrit
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navigationItems.map((item, index) => (
                <NavigationItem key={index} {...item} showNav={showNav} isDark={isDark} />
              ))}
              <div
                className={clsx('h-6 w-px', showNav ? 'bg-gray-300' : 'bg-white/40')}
              />
              <div className="flex items-center gap-2">
                <Socials fill={showNav ? '#047857' : 'white'} />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-3">
              <button
                onClick={handleBookingClick}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg"
              >
                {t('common:book-now')}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={clsx(
                  'p-2 rounded-lg transition-colors',
                  showNav
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10',
                )}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      <MobileNavigationDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </>
  );
};

interface NavigationItemProps {
  title: string;
  path: string;
  children?: { title: string; path: string }[];
  showNav: boolean;
  isDark: boolean;
}

const NavigationItem: React.FC<NavigationItemProps> = ({
  title,
  path,
  children,
  showNav,
  isDark,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname.startsWith(path);

  return (
    <div
      className="relative py-2"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <a
        href={path}
        className={clsx(
          'text-sm font-medium tracking-wide hover:opacity-70 transition-opacity',
          showNav ? 'text-gray-700' : 'text-white',
          isDark && 'text-gray-700',
          isActive && 'border-b-2 border-emerald-600 pb-1',
        )}
      >
        {title}
      </a>

      {children && children.length > 0 && isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200">
          {children.map((child, index) => (
            <a
              key={index}
              href={child.path}
              className={clsx(
                'block px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-800 transition-colors',
                location.pathname === child.path &&
                  'bg-emerald-50 text-emerald-800 font-semibold',
              )}
            >
              {child.title}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

interface MobileNavigationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileNavigationDrawer: React.FC<MobileNavigationDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close menu"
        className={clsx(
          'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 md:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape' || e.key === 'Enter') {
            onClose();
          }
        }}
      />

      {/* Drawer */}
      <div
        className={clsx(
          'fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <span className="text-xl font-serif text-emerald-800">Menu</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {navigationItems.map((item, index) => (
                <MobileNavigationItem key={index} {...item} onClose={onClose} />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 space-y-4">
            {isInquiryModeEnabled ? (
              <button
                onClick={() => {
                  onOpenInquiry();
                  onClose();
                }}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-6 py-3 rounded-lg font-semibold text-sm tracking-wide shadow-lg transition-all"
              >
                Express Interest
              </button>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => {
                    window.location.href = '/packages/reservation';
                    onClose();
                  }}
                  className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-6 py-3 rounded-lg font-semibold text-sm tracking-wide shadow-lg transition-all"
                >
                  Book Now
                </button>
                <button
                  onClick={() => {
                    onOpenInquiry();
                    onClose();
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium text-sm tracking-wide transition-all"
                >
                  Express Interest
                </button>
              </div>
            )}
            <div className="flex items-center justify-center gap-4 pt-2">
              <Socials fill="#047857" />
            </div>
            <div className="text-center text-sm text-gray-600">
              <p>
                <a
                  href="tel:+918485835675"
                  className="hover:text-emerald-700 inline-flex items-center gap-1"
                >
                  <Sprite
                    name="phone"
                    width="16"
                    height="16"
                    className="w-3.5 h-3.5 fill-current"
                  />
                  <span>/</span>
                  <Sprite name="whatsapp" width="24" height="24" className="w-4 h-4" />
                  <span>+91 84858 35675</span>
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface MobileNavigationItemProps {
  title: string;
  path: string;
  children?: { title: string; path: string }[];
  onClose: () => void;
}

const MobileNavigationItem: React.FC<MobileNavigationItemProps> = ({
  title,
  path,
  children,
  onClose,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const isActive = location.pathname === path;

  if (!children || children.length === 0) {
    return (
      <a
        href={path}
        className={clsx(
          'block px-4 py-3 rounded-lg transition-colors font-medium',
          isActive
            ? 'bg-emerald-50 text-emerald-800 font-semibold'
            : 'text-gray-700 hover:bg-emerald-50',
        )}
        onClick={onClose}
      >
        {title}
      </a>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors font-medium',
          isActive
            ? 'text-emerald-800 bg-emerald-50/50'
            : 'text-gray-700 hover:bg-emerald-50',
        )}
      >
        <span className={isActive ? 'font-semibold' : ''}>{title}</span>
        <svg
          className={clsx('w-5 h-5 transition-transform', isExpanded && 'rotate-180')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-emerald-200 pl-2">
          {children.map((child, index) => {
            const isChildActive = location.pathname === child.path;
            return (
              <a
                key={index}
                href={child.path}
                className={clsx(
                  'block px-4 py-2.5 text-sm rounded-lg transition-colors',
                  isChildActive
                    ? 'bg-emerald-50 text-emerald-800 font-semibold'
                    : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700',
                )}
                onClick={onClose}
              >
                {child.title}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};
